/**
 * /verify-email?token=… — landing page for the link in the verification
 * email. Consumes the token on mount, then shows a success or failure
 * state. The page is publicly accessible because the user might open it
 * in a different browser than the one they signed up in.
 */

import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";

import { AuthShell } from "../components/AuthShell";
import { Button, Icon, Spinner, Stack } from "../components/ui";
import { t } from "../i18n";
import { trpc } from "../lib/trpc";

interface VerifyEmailSearch {
  token?: string | undefined;
}

export const Route = createFileRoute("/verify-email")({
  validateSearch: (raw: Record<string, unknown>): VerifyEmailSearch => ({
    token: typeof raw.token === "string" ? raw.token : undefined,
  }),
  component: VerifyEmailPage,
});

type VerifyState =
  | { status: "verifying" }
  | { status: "success" }
  | { status: "failure"; message: string };

function VerifyEmailPage() {
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const search = Route.useSearch();
  const [state, setState] = useState<VerifyState>(
    search.token
      ? { status: "verifying" }
      : { status: "failure", message: t.resetPassword.invalidLink },
  );
  const verify = trpc.auth.verifyEmail.useMutation();
  const triggered = useRef(false);

  // The mutation must run exactly once per token. React 18's strict-mode
  // double-render of effects in dev would otherwise hit the API twice and
  // the second call would (correctly) report a consumed-token failure.
  useEffect(() => {
    if (!search.token || triggered.current) return;
    triggered.current = true;
    verify.mutate(
      { token: search.token },
      {
        onSuccess: () => {
          utils.auth.me.invalidate();
          setState({ status: "success" });
        },
        onError: (error) => {
          setState({ status: "failure", message: error.message });
        },
      },
    );
  }, [search.token, verify, utils.auth.me]);

  if (state.status === "verifying") {
    return (
      <AuthShell title={t.verifyEmail.title} description={t.verifyEmail.verifyingDescription}>
        <div className="flex justify-center py-6">
          <Spinner size="md" />
        </div>
      </AuthShell>
    );
  }

  if (state.status === "success") {
    return (
      <AuthShell title={t.verifyEmail.successTitle} description={t.verifyEmail.successDescription}>
        <div className="mb-4 flex justify-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
            <Icon name="check-circle" size="lg" />
          </div>
        </div>
        <Button
          variant="primary"
          size="lg"
          className="w-full"
          onClick={() => navigate({ to: "/" })}
        >
          {t.verifyEmail.continueButton}
        </Button>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title={t.verifyEmail.failureTitle}
      description={t.verifyEmail.failureDescription(state.message)}
    >
      <Stack gap={3}>
        <Link
          to="/"
          className="inline-flex h-11 items-center justify-center rounded-md bg-primary px-4 font-medium text-primary-fg text-sm hover:opacity-90"
        >
          {t.login.backToLogin}
        </Link>
      </Stack>
    </AuthShell>
  );
}
